import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FaArrowLeft, FaBuilding, FaLeaf, FaLock } from 'react-icons/fa';
import api from '../services/api';
import { useTenant } from '../contexts/TenantContext';
import './Login.css';

export default function ResetPassword() {
  const { tenant, setTenant, tenants, tenantsLoading, tenantsError } = useTenant();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const token = params.get('token') || '';
  const tenantParam = params.get('tenant') || '';

  const [novaSenha, setNovaSenha] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const tenantOptions = useMemo(
    () => tenants.map((item) => ({
      id: item.id,
      name: item.name ?? item.nome ?? item.id,
    })),
    [tenants]
  );

  useEffect(() => {
    if (tenantParam) {
      setTenant(tenantParam);
    }
  }, [tenantParam, setTenant]);

  useEffect(() => {
    if (!token) {
      setMessage('Token ausente ou inválido.');
      setIsSuccess(false);
    }
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    setIsSuccess(false);

    if (!tenant) {
      setMessage('Selecione o cliente para continuar.');
      return;
    }

    if (!token) {
      setMessage('Token ausente ou inválido.');
      return;
    }

    if (novaSenha.length < 6) {
      setMessage('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    if (novaSenha !== confirmar) {
      setMessage('As senhas não conferem.');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const response = await api.post('/auth/reset-password', { token, novaSenha });
      setIsSuccess(true);
      setMessage(response.data?.message || 'Senha redefinida com sucesso.');
      setTimeout(() => navigate('/login'), 1200);
    } catch (error) {
      setMessage(error?.response?.data || 'Token inválido, expirado ou senha inválida.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-copy" aria-label="Nova senha Cultiva Mais">
        <div className="login-copy-badge">
          <FaLeaf />
          Acesso seguro
        </div>
        <h1>Defina uma nova senha.</h1>
        <p>
          Crie uma senha segura para voltar a acessar sua operação no Cultiva Mais.
        </p>
      </section>

      <section className="login-card" aria-labelledby="reset-password-title">
        <div className="login-brand">
          <img src="/CultivaMaisLogo.png" alt="Cultiva Mais" />
        </div>

        <div className="login-heading">
          <h2 id="reset-password-title">Definir nova senha</h2>
          <p>Informe e confirme sua nova senha para concluir a recuperação.</p>
        </div>

        {(message || tenantsError) && (
          <div className={`login-alert ${isSuccess ? 'is-success' : ''}`} role="alert" aria-live="assertive">
            <strong>{isSuccess ? 'Tudo certo.' : 'Atenção.'}</strong>
            <span>{tenantsError || message}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="login-field">
            <label htmlFor="reset-tenant">Cliente</label>
            <div className={`login-control ${message && !tenant ? 'has-error' : ''}`}>
              <FaBuilding className="login-control-icon" aria-hidden="true" />
              <select
                id="reset-tenant"
                value={tenant}
                onChange={(event) => {
                  setTenant(event.target.value);
                  setMessage('');
                }}
                disabled={tenantsLoading || loading}
                required
                aria-invalid={Boolean(message && !tenant)}
              >
                <option value="">
                  {tenantsLoading ? 'Carregando clientes...' : 'Selecione o cliente'}
                </option>
                {tenantOptions.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="login-field">
            <label htmlFor="reset-password">Nova senha</label>
            <div className="login-control">
              <FaLock className="login-control-icon" aria-hidden="true" />
              <input
                id="reset-password"
                type="password"
                placeholder="Digite a nova senha"
                value={novaSenha}
                onChange={(event) => {
                  setNovaSenha(event.target.value);
                  setMessage('');
                }}
                autoComplete="new-password"
                disabled={loading || !token}
                required
              />
            </div>
          </div>

          <div className="login-field">
            <label htmlFor="reset-confirm-password">Confirmar senha</label>
            <div className="login-control">
              <FaLock className="login-control-icon" aria-hidden="true" />
              <input
                id="reset-confirm-password"
                type="password"
                placeholder="Confirme a nova senha"
                value={confirmar}
                onChange={(event) => {
                  setConfirmar(event.target.value);
                  setMessage('');
                }}
                autoComplete="new-password"
                disabled={loading || !token}
                required
              />
            </div>
          </div>

          <button type="submit" className="login-button" disabled={loading || !token}>
            {loading && <span className="login-spinner" aria-hidden="true" />}
            {loading ? 'Redefinindo...' : 'Redefinir senha'}
          </button>
        </form>

        <div className="login-support">
          <Link className="login-back-link" to="/login">
            <FaArrowLeft aria-hidden="true" />
            Voltar ao login
          </Link>
        </div>
      </section>
    </main>
  );
}
